import { createColumnHelper } from "@tanstack/react-table";
import type { TFunction } from "i18next";

import { BranchColumnFilter } from "src/asyncFilters";
import {
    DateColumnFilter,
    LabelValuePair,
    MultiSelectColumnFilter,
    SelectColumnFilter,
    TableLink,
    TabularText,
    TextColumnFilter,
    Tooltip,
} from "src/components";
import { routes } from "src/routes";
import { Color } from "src/theme";
import { type SpecialPriceListDto, CustomerTypeEnum, SpecialPriceStatusEnum } from "src/types";
import { dateTimeFormatter, enumToLocalizedOptions, getTranslatedEnumValue } from "src/utils";

const columnHelper = createColumnHelper<SpecialPriceListDto>();

export const createColumns = (t: TFunction) => [
    columnHelper.accessor("customer.name", {
        id: "customerName",
        header: t("dashboard.allSpecialPrices.table.customerName") as string,
        cell: ({ row }) => (
            <TableLink to={routes.customerDetail.fillPathParams({ customerId: row.original.customer.id.toString() })}>
                {row.original.customer.name}
            </TableLink>
        ),
        meta: {
            filterComponent: TextColumnFilter,
            minWidth: 120,
        },
    }),
    columnHelper.accessor("customer.acn", {
        id: "customerAcn",
        header: t("dashboard.allSpecialPrices.table.shipToAcn") as string,
        meta: {
            filterComponent: TextColumnFilter,
            minWidth: 120,
        },
    }),
    columnHelper.accessor("applyToShipTos", {
        id: "applyToShipTos",
        header: t("dashboard.allSpecialPrices.table.appliedTo") as string,
        cell: ({ row }) =>
            !!row.original.applyToShipTos?.length && (
                <Tooltip
                    withinPortal={false}
                    openDelay={500}
                    backgroundColor={Color.accent50}
                    color={Color.supportGraphite500}
                    label={
                        <TabularText columnGap="0.5rem">
                            {row.original.applyToShipTos.map((shipTo) => (
                                <LabelValuePair key={shipTo.id} label={shipTo.acn} value={shipTo.name} />
                            ))}
                        </TabularText>
                    }
                >
                    <div>{row.original.applyToShipTos.length}</div>
                </Tooltip>
            ),
        enableColumnFilter: false,
    }),
    columnHelper.accessor("productPackageName", {
        id: "productPackageName",
        header: t("dashboard.allSpecialPrices.table.productPackageName") as string,
        meta: {
            filterComponent: TextColumnFilter,
        },
    }),
    columnHelper.accessor("validFrom", {
        id: "validFrom",
        header: t("dashboard.allSpecialPrices.table.validFrom") as string,
        cell: ({ row }) => dateTimeFormatter(row.original.validFrom),
        meta: {
            filterComponent: DateColumnFilter,
        },
    }),
    columnHelper.accessor("validTo", {
        id: "validTo",
        header: t("dashboard.allSpecialPrices.table.validTo") as string,
        cell: ({ row }) => dateTimeFormatter(row.original.validTo),
        meta: {
            filterComponent: DateColumnFilter,
        },
    }),
    columnHelper.accessor("status", {
        id: "status",
        header: t("dashboard.allSpecialPrices.table.status") as string,
        cell: (cellContext) => getTranslatedEnumValue("specialPriceStatus", cellContext.getValue()),
        meta: {
            minWidth: 100,
            collapse: true,
            options: enumToLocalizedOptions("specialPriceStatus", SpecialPriceStatusEnum),
            filterComponent: SelectColumnFilter,
        },
    }),
    columnHelper.accessor("customer.branch", {
        id: "customerBranches",
        header: t("dashboard.allSpecialPrices.table.customerBranch") as string,
        cell: ({ row }) =>
            row.original.customer.branch ? `${row.original.customer.branch.code} - ${row.original.customer.branch.name}` : "",
        meta: {
            filterComponent: BranchColumnFilter,
            minWidth: 140,
        },
    }),
    columnHelper.accessor("customer.type", {
        id: "customerTypes",
        header: t("dashboard.allSpecialPrices.table.customerType") as string,
        cell: (cellContext) => getTranslatedEnumValue("CustomerTypeEnum", cellContext.getValue()),
        meta: {
            filterComponent: MultiSelectColumnFilter,
            options: enumToLocalizedOptions("CustomerTypeEnum", CustomerTypeEnum),
            minWidth: 140,
        },
    }),
    columnHelper.accessor("customer.category", {
        id: "category",
        header: t("dashboard.allSpecialPrices.table.category") as string,
        cell: (cellContext) => getTranslatedEnumValue("customerCategory", cellContext.getValue()),
        enableColumnFilter: false,
    }),
    columnHelper.accessor("contractId", {
        id: "contractId",
        header: t("dashboard.allSpecialPrices.table.contractId") as string,
        meta: {
            filterComponent: TextColumnFilter,
            minWidth: 90,
        },
    }),
    columnHelper.accessor("bulkId", {
        id: "bulkId",
        header: t("dashboard.allSpecialPrices.table.bulkId") as string,
        meta: {
            filterComponent: TextColumnFilter,
            minWidth: 90,
        },
    }),
];
